import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, Text, ScrollView, Platform, Dimensions, Modal, Pressable, StyleSheet } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../theme";
import styles from "./TicketingPortalScreen.style";
import mobileStyles from "./TicketingPortalScreen.mobile.style";
import useTicketingPortalData from "./hooks/useTicketingPortalData";
import useTicketingPortalLandingModel, { LandingActionView } from "./hooks/useTicketingPortalLandingModel";
import { RootState, AppDispatch } from "../../store";
import { setSelectedTicketId, setTicketDetailSource } from "../../store/slices/ticketingPortalSlice";
import {
  fetchSustainTeamMembers,
  fetchSustainQueue,
  fetchMyTickets,
  fetchActiveNotifications,
  fetchTicketingTeams,
  fetchAvailableTicketingTeams,
} from "../../store/thunks/ticketingPortalThunk";
import { logger } from "../../utils/logger";

// Import components
import LHLoader from "../../components/LHLoader";
import IncidentBanner from "./components/IncidentBanner";
import HomepageNotifBannerList from "./components/HomepageNotifBannerList";
import KpiStrip from "./components/KpiStrip";
import ActionTile from "./components/ActionTile";
import { useToast } from "../../components/LhToastContainer";
import useTicketingAccess from "./hooks/useTicketingAccess";

type Props = {
  onNavigate?: (screen: string) => void;
  /** Notification id targeted by a deep link (e.g. a Teams "View Details" button). */
  notificationId?: string;
  isDeepLinked?: boolean;
};

const TicketingPortalScreen: React.FC<Props> = ({ onNavigate, notificationId, isDeepLinked }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const toast = useToast();
  const { isSustainTeamMember, canViewReportingAnalytics } = useTicketingAccess();
  const { ticketingData, loading, error } = useTicketingPortalData();
  const landing = useTicketingPortalLandingModel(ticketingData);
  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );

  const windowWidth = Dimensions.get("window").width;
  const isiPhone = Platform.OS === "ios" && windowWidth < 768;
  const userEmail = useSelector(
    (state: RootState) => state.appData?.userInfo?.email ?? ""
  );
  const sustainTeamMember = useSelector(
    (state: RootState) => state.appData?.userInfo?.sustainTeamMember
  );
  const roleName = useSelector(
    (state: RootState) => state.appData?.userInfo?.roleName
  );
  const userRole = sustainTeamMember ? "Sustain Team" : (roleName ?? "Field User");

  // Detect whether the current Sustain Team member has a team assigned
  const sustainTeamMembers = useSelector(
    (state: RootState) => state.ticketingPortal.sustainTeamMembers
  );
  const availableTicketingTeams = useSelector(
    (state: RootState) => state.ticketingPortal.availableTicketingTeams
  );
  const activeTeamIds = useMemo(
    () => new Set(availableTicketingTeams.map((team) => team.id)),
    [availableTicketingTeams]
  );
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const currentUserHasTeamAssigned = (() => {
    if (!sustainTeamMember || !teamsLoaded) return true; // non-sustain users and pending team data are unaffected
    const me = sustainTeamMembers.find(
      (m) => m.userEmail.toLowerCase() === (userEmail ?? "").toLowerCase()
    );
    return me?.teamId != null && activeTeamIds.has(me.teamId);
  })();

  const [showNoTeamModal, setShowNoTeamModal] = useState(false);

  /**
   * Guard navigation to pages that require an active team assignment (Sustain
   * Queue and Notifications & Comms). If the logged-in Sustain Team member has
   * no team assigned, or their assigned team is inactive, display the attention
   * modal instead of navigating.
   */
  const handleSustainTeamPageNavigation = (screen: string) => {
    if (isSustainTeamMember && !currentUserHasTeamAssigned) {
      setShowNoTeamModal(true);
      return;
    }
    onNavigate?.(screen);
  };

  // Pre-fetch sustain team members so the team count badge on the
  // Team Management action tile is populated before the user navigates.
  useEffect(() => {
    dispatch(fetchSustainTeamMembers());
  }, [dispatch]);

  // Load teams available for the queue and actionable ticket workflows.
  useEffect(() => {
    let cancelled = false;
    dispatch(fetchTicketingTeams()).catch(() => {
      logger.warn("Failed to load sustain team catalog");
    });
    dispatch(fetchAvailableTicketingTeams())
      .then(() => {
        if (!cancelled) setTeamsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setTeamsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Fetch active incident and maintenance notifications to display as banners
  // on the homepage without requiring user to click "Notifications & Comms" card
  const [notifFetchDone, setNotifFetchDone] = useState(false);
  useEffect(() => {
    (dispatch(fetchActiveNotifications()) as unknown as Promise<void>)
      .then(() => setNotifFetchDone(true))
      .catch(() => setNotifFetchDone(true));
  }, [dispatch]);

  const homepageBannerNotifs = useSelector(
    (state: RootState) => state.ticketingPortal.homepageBannerNotifs
  );
  const [preExpandId, setPreExpandId] = useState<string | null>(null);
  const deepLinkHandledRef = useRef(false);

  // Teams "View Details" / direct-link support: pre-expand the targeted notification,
  // or show a red toast when it's no longer active (expired).
  useEffect(() => {
    if (!isDeepLinked || !notificationId || !notifFetchDone || deepLinkHandledRef.current) return;
    deepLinkHandledRef.current = true;
    const match = homepageBannerNotifs.find((b) => b.id === notificationId);
    if (match) {
      setPreExpandId(notificationId);
    } else {
      toast.show("This message has expired!", "error");
    }
  }, [isDeepLinked, notificationId, notifFetchDone, homepageBannerNotifs, toast]);

  // Mobile-only: lets the banner list scroll the page back to top when a message collapses.
  const mobileScrollRef = useRef<ScrollView>(null);
  const scrollMobileToTop = () => mobileScrollRef.current?.scrollTo?.({ y: 0, animated: true });

  // Always re-fetch the sustain queue on every portal screen mount so the
  // active count badge stays current and the queue holds the latest data.
  useEffect(() => {
    dispatch(fetchSustainQueue());
  }, [dispatch]);

  // Pre-fetch My Tickets so the tile shows a live count instead of "0".
  useEffect(() => {
    dispatch(fetchMyTickets({
      pageSize: 100,
      pageNumber: 1,
      sortBy: "createdAt",
      sortOrder: "DESC",
      ...(userEmail ? { submitterEmail: userEmail } : {}),
    }));
  }, [dispatch, userEmail]);

  // Navigate to the ticket detail for the most recent widespread banner
  function handleBannerViewPress() {
    const lastBanner = landing.widespreadBanners[landing.widespreadBanners.length - 1];
    if (lastBanner?.ticketId) {
      dispatch(setSelectedTicketId(lastBanner.ticketId));
      dispatch(setTicketDetailSource("Ticketing Portal"));
      onNavigate?.("Ticket Detail");
    }
  }

  if (loading) {
    return <LHLoader />;
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Error loading Ticketing Portal: {error}</Text>
      </View>
    );
  }

  /* ── iPhone mobile layout ─────────────────────────────────── */
  if (isiPhone) {
    const locationLabel = selectedLocation?.name || selectedLocation?.displayText || "";
    const roleLabel = locationLabel ? `${userRole} - ${locationLabel}` : userRole;

    return (
      <View style={[styles.screenRoot, isDark && { backgroundColor: colors.background.page }]}>
        <View style={styles.container}>
          <ScrollView ref={mobileScrollRef} style={styles.content} contentContainerStyle={styles.scrollContent}>
            {/* Hero card */}
            <View
              style={[
                mobileStyles.heroBlock,
                isDark && {
                  backgroundColor: colors.background.surface,
                  borderWidth: 1,
                  borderColor: colors.border.default,
                },
              ]}
            >
              <Text
                style={[
                  mobileStyles.heroLabel,
                  isDark && { color: colors.brand.primary },
                ]}
              >
                TICKETING PORTAL
              </Text>
              <Text
                style={[
                  mobileStyles.heroGreeting,
                  isDark && { color: colors.text.primary },
                ]}
              >
                {landing.greeting}
              </Text>
              <Text
                style={[
                  mobileStyles.heroSubtitle,
                  isDark && { color: colors.text.secondary },
                ]}
              >
                {landing.subtitle}
              </Text>
              <View
                style={[
                  mobileStyles.heroChip,
                  isDark && { backgroundColor: colors.background.elevated },
                ]}
              >
                <MaterialIcons
                  name="person-outline"
                  size={14}
                  color={isDark ? colors.text.secondary : "#D4E2F2"}
                />
                <Text
                  style={[
                    mobileStyles.heroChipText,
                    isDark && { color: colors.text.secondary },
                  ]}
                >
                  {roleLabel}
                </Text>
              </View>
            </View>

            {/* Incident banner – mobile variant */}
            {/* {landing.showIncidentBanner ? (
              <IncidentBanner
                incident={landing.incident}
                mobile
                onViewPress={handleBannerViewPress}
                onAffectedPress={handleBannerViewPress}
              />
            ) : null} */}

            {/* Homepage notification banners from Notifications & Comms */}
            <HomepageNotifBannerList onNavigate={onNavigate} preExpandId={preExpandId} onCollapse={scrollMobileToTop} />

            {/* KPI cards – unchanged */}
            {landing.showKpis ? <KpiStrip items={landing.kpis} loading={landing.kpisLoading} /> : null}

            <Text style={[styles.sectionLabel, isDark && { color: colors.text.secondary }]}>WHAT WOULD YOU LIKE TO DO?</Text>

            {/* Compact horizontal action tiles */}
            <View style={mobileStyles.gridContainer}>
              {/* Reporting and Analytics is desktop/tablet only */}
              {landing.actions
                .filter((action: LandingActionView) => action.id !== "reporting-analytics")
                .map((action: LandingActionView) => (
                <ActionTile
                  key={action.id}
                  action={action}
                  mobile
                  onPress={() => {
                    if (action.id === "submit-ticket") {
                      onNavigate?.("Submit Ticket");
                    }
                    if (action.id === "my-tickets") {
                      onNavigate?.("My Tickets");
                    }
                    if (action.id === "sustain-queue") {
                      handleSustainTeamPageNavigation("Sustain Queue");
                    }
                    if (action.id === "notifications-comms") {
                      handleSustainTeamPageNavigation("Notifications & Comms");
                    }
                    if (action.id === "team-management") {
                      if (!isSustainTeamMember) {
                        toast.show(
                          "Access denied. You do not have permission to view Sustain Team Management.",
                          "error"
                        );
                        return;
                      }
                      onNavigate?.("Team Management");
                    }
                    if (action.id === "reporting-analytics") {
                      if (!canViewReportingAnalytics) {
                        toast.show(
                          "Access denied. You do not have permission to view Reporting and Analytics.",
                          "error"
                        );
                        return;
                      }
                      onNavigate?.("Ticketing Analytics Report");
                    }
                    if (action.id === "ticketing-configurations") {
                      if (!isSustainTeamMember) {
                        toast.show(
                          "Access denied. You do not have permission to view Ticketing Configurations.",
                          "error"
                        );
                        return;
                      }
                      onNavigate?.("Ticketing Configurations");
                    }
                  }}
                />
              ))}
            </View>
          </ScrollView>
        </View>

        {/* No Team Assigned Modal – mobile */}
        <Modal
          visible={showNoTeamModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowNoTeamModal(false)}
        >
          <View style={ntmStyles.overlay}>
            <View style={ntmStyles.card}>
              <View style={ntmStyles.iconRow}>
                <MaterialIcons name="warning" size={28} color="#FF8C09" />
              </View>
              <Text style={ntmStyles.title}>Attention</Text>
              <Text style={ntmStyles.body}>
                You Do Not Currently Belong to an Active Sustain Team, Please Navigate to Sustain Team Management and Select a Team Assignment
              </Text>
              <View style={ntmStyles.actions}>
                <Pressable
                  style={ntmStyles.btnCancel}
                  onPress={() => setShowNoTeamModal(false)}
                >
                  <Text style={ntmStyles.btnCancelText}>Cancel</Text>
                </Pressable>
                <Pressable
                  style={ntmStyles.btnPrimary}
                  onPress={() => {
                    setShowNoTeamModal(false);
                    onNavigate?.("Team Management");
                  }}
                >
                  <Text style={ntmStyles.btnPrimaryText}>Sustain Team Management</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      </View>
    );

  } // end isiPhone

  /* ── Default layout (web / iPad) ──────────────────────────── */
  return (
    <View style={[styles.screenRoot, isDark && { backgroundColor: colors.background.page }]}>
      <View style={styles.container}>
        <ScrollView style={styles.content} contentContainerStyle={styles.scrollContent}>
          <View style={styles.heroBlock}>
            <Text style={[styles.heroGreeting, isDark && { color: colors.text.primary }]}>{landing.greeting}</Text>
            <Text style={[styles.heroSubtitle, isDark && { color: colors.text.secondary }]}>{landing.subtitle}</Text>
          </View>

          {/* Homepage notification banners from Notifications & Comms */}
          <HomepageNotifBannerList onNavigate={onNavigate} preExpandId={preExpandId} />

          {landing.showKpis ? <KpiStrip items={landing.kpis} loading={landing.kpisLoading} /> : null}

          <Text style={[styles.sectionLabel, isDark && { color: colors.text.secondary }]}>WHAT WOULD YOU LIKE TO DO?</Text>

          <View style={styles.gridContainer}>
            {landing.actions.map((action: LandingActionView) => (
              <ActionTile
                key={action.id}
                action={action}
                onPress={() => {
                  if (action.id === "submit-ticket") {
                    onNavigate?.("Submit Ticket");
                  }
                  if (action.id === "my-tickets") {
                    onNavigate?.("My Tickets");
                  }
                  if (action.id === "sustain-queue") {
                    handleSustainTeamPageNavigation("Sustain Queue");
                  }
                  if (action.id === "notifications-comms") {
                    handleSustainTeamPageNavigation("Notifications & Comms");
                  }
                  if (action.id === "team-management") {
                    if (!isSustainTeamMember) {
                      toast.show(
                        "Access denied. You do not have permission to view Sustain Team Management.",
                        "error"
                      );
                      return;
                    }
                    onNavigate?.("Team Management");
                  }
                  if (action.id === "reporting-analytics") {
                    if (!canViewReportingAnalytics) {
                      toast.show(
                        "Access denied. You do not have permission to view Reporting and Analytics.",
                        "error"
                      );
                      return;
                    }
                    onNavigate?.("Ticketing Analytics Report");
                  }
                  if (action.id === "ticketing-configurations") {
                    if (!isSustainTeamMember) {
                      toast.show(
                        "Access denied. You do not have permission to view Ticketing Configurations.",
                        "error"
                      );
                      return;
                    }
                    onNavigate?.("Ticketing Configurations");
                  }
                }}
              />
            ))}
          </View>
        </ScrollView>
      </View>

      {/* No Team Assigned Modal */}
      <Modal
        visible={showNoTeamModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowNoTeamModal(false)}
      >
        <View style={ntmStyles.overlay}>
          <View style={ntmStyles.card}>
            <View style={ntmStyles.iconRow}>
              <MaterialIcons name="warning" size={28} color="#FF8C09" />
            </View>
            <Text style={ntmStyles.title}>Attention</Text>
            <Text style={ntmStyles.body}>
              You Do Not Currently Belong to an Active Sustain Team, Please Navigate to Sustain Team Management and Select a Team Assignment
            </Text>
            <View style={ntmStyles.actions}>
              <Pressable
                style={ntmStyles.btnCancel}
                onPress={() => setShowNoTeamModal(false)}
              >
                <Text style={ntmStyles.btnCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={ntmStyles.btnPrimary}
                onPress={() => {
                  setShowNoTeamModal(false);
                  onNavigate?.("Team Management");
                }}
              >
                <Text style={ntmStyles.btnPrimaryText}>Sustain Team Management</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

/* ── No Team Assigned modal styles ──────────────────────────────────────────── */
const ntmStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 14,
    padding: 24,
    width: "100%",
    maxWidth: 440,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 20,
    elevation: 10,
    alignItems: "center",
  },
  iconRow: {
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#1a2332",
    marginBottom: 12,
    textAlign: "center",
  },
  body: {
    fontSize: 14,
    color: "#374151",
    lineHeight: 22,
    marginBottom: 24,
    textAlign: "center",
  },
  actions: {
    flexDirection: "row",
    gap: 12,
    justifyContent: "center",
    flexWrap: "wrap",
    width: "100%",
  },
  btnCancel: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
  },
  btnCancelText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#374151",
  },
  btnPrimary: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: "#034895",
    flex: 1,
    alignItems: "center",
  },
  btnPrimaryText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#ffffff",
    textAlign: "center",
  },
});

export default TicketingPortalScreen;
